package com.schoolconnect.app.repository.files

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.FileUploadRequest
import com.schoolconnect.app.datasource.network.response.FileConfirmResponse
import com.schoolconnect.app.datasource.network.response.FileDownloadUrlResponse
import com.schoolconnect.app.datasource.network.response.FileListResponse
import com.schoolconnect.app.datasource.network.response.FileObjectResponse
import com.schoolconnect.app.datasource.network.response.FileRestoreResponse
import com.schoolconnect.app.datasource.network.response.FileUploadGrant
import com.schoolconnect.app.util.scLog

data class FileUploadSession(
    val module: String,
    val recordId: String? = null,
    val uploadedFileIds: List<String> = emptyList()
)

data class UploadedFile(
    val fileId: String,
    val recordId: String,
    val confirmation: FileConfirmResponse
)

/** Shared file orchestration for any module using the reservation flow. */
object FileUploadRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] FileUploadRepository"

    fun newSession(module: String): FileUploadSession {
        val normalizedModule = module.trim()
        require(normalizedModule.isNotBlank()) { "module must not be blank" }
        return FileUploadSession(module = normalizedModule)
    }

    suspend fun requestUpload(
        session: FileUploadSession,
        filename: String,
        contentType: String,
        sizeBytes: Long
    ): Result<FileUploadGrant> = KtorApiClient.requestFileUpload(
        FileUploadRequest(
            module = session.module,
            recordId = session.recordId,
            filename = filename,
            contentType = contentType,
            sizeBytes = sizeBytes
        )
    )

    /** Requests, uploads, confirms, and advances the session reservation. */
    suspend fun upload(
        session: FileUploadSession,
        filename: String,
        contentType: String,
        bytes: ByteArray
    ): Result<Pair<FileUploadSession, UploadedFile>> {
        if (bytes.isEmpty()) return Result.failure(IllegalArgumentException("File must not be empty"))
        val grant = requestUpload(session, filename, contentType, bytes.size.toLong()).getOrElse { return Result.failure(it) }
        val fileId = grant.fileId?.trim().orEmpty()
        if (fileId.isBlank()) return Result.failure(IllegalStateException("Upload grant did not contain file_id"))

        val nextRecordId = session.recordId ?: grant.reservedEntityId?.trim()?.takeIf { it.isNotBlank() }
        if (nextRecordId.isNullOrBlank()) {
            return Result.failure(IllegalStateException("First upload did not contain reserved_entity_id"))
        }

        KtorApiClient.uploadFileBytes(grant, bytes, contentType).getOrElse { return Result.failure(it) }
        val confirmation = KtorApiClient.confirmFileUpload(fileId).getOrElse { return Result.failure(it) }
        val nextSession = session.copy(
            recordId = nextRecordId,
            uploadedFileIds = session.uploadedFileIds + fileId
        )
        scLog("$TAG upload confirmed module=${session.module} recordId=$nextRecordId fileCount=${nextSession.uploadedFileIds.size}")
        return Result.success(nextSession to UploadedFile(fileId, nextRecordId, confirmation))
    }

    /** Uploads and confirms a file before the owning record exists. */
    suspend fun uploadStandalone(
        module: String,
        filename: String,
        contentType: String,
        bytes: ByteArray
    ): Result<String> {
        if (bytes.isEmpty()) return Result.failure(IllegalArgumentException("File must not be empty"))
        val session = newSession(module)
        val grant = requestUpload(session, filename, contentType, bytes.size.toLong()).getOrElse { return Result.failure(it) }
        val fileId = grant.fileId?.trim().orEmpty()
        if (fileId.isBlank()) return Result.failure(IllegalStateException("Upload grant did not contain file_id"))
        KtorApiClient.uploadFileBytes(grant, bytes, contentType).getOrElse { return Result.failure(it) }
        KtorApiClient.confirmFileUpload(fileId).getOrElse { return Result.failure(it) }
        return Result.success(fileId)
    }

    /** Uses the dedicated current-user profile-picture endpoint, then uploads and confirms the file. */
    suspend fun uploadProfilePicture(
        recordId: String?,
        filename: String,
        contentType: String,
        bytes: ByteArray
    ): Result<String> {
        if (bytes.isEmpty()) return Result.failure(IllegalArgumentException("File must not be empty"))
        val grant = KtorApiClient.requestProfilePictureUpload(
            FileUploadRequest(
                module = "PROFILE",
                recordId = recordId?.trim()?.takeIf { it.isNotBlank() },
                filename = filename,
                contentType = contentType,
                sizeBytes = bytes.size.toLong()
            )
        ).getOrElse { return Result.failure(it) }
        val fileId = grant.fileId?.trim().orEmpty()
        if (fileId.isBlank()) return Result.failure(IllegalStateException("Profile upload grant did not contain file_id"))
        KtorApiClient.uploadFileBytes(grant, bytes, contentType).getOrElse { return Result.failure(it) }
        KtorApiClient.confirmFileUpload(fileId).getOrElse { return Result.failure(it) }
        scLog("$TAG profile picture upload confirmed recordId=${recordId ?: "<current-user>"}")
        return Result.success(fileId)
    }

    suspend fun getMetadata(fileId: String): Result<FileObjectResponse> = KtorApiClient.getFileMetadata(fileId)
    suspend fun getDownloadUrl(fileId: String): Result<FileDownloadUrlResponse> = KtorApiClient.getFileDownloadUrl(fileId)
    suspend fun downloadBytes(url: String): Result<ByteArray> = KtorApiClient.downloadAssetBytes(url)
    suspend fun listRecordFiles(recordId: String, module: String? = null): Result<FileListResponse> =
        KtorApiClient.listRecordFiles(recordId, module)
    suspend fun delete(fileId: String): Result<Unit> = KtorApiClient.deleteFile(fileId)
    suspend fun restore(fileId: String): Result<FileRestoreResponse> = KtorApiClient.restoreFile(fileId)
}
